import _defineProperty from "@babel/runtime/helpers/defineProperty";
import { ExposureType } from '../index';
import hashFn from '../util/hash';
export const ONE_HOUR = 1000 * 60 * 60;
export default class ExposureEvents {
  constructor(analyticsWebClient) {
    _defineProperty(this, "spamBuster", {});
    _defineProperty(this, "automaticExposuresEnabled", false);
    _defineProperty(this, "automaticExposuresPredicate", () => true);
    this.analyticsWebClient = analyticsWebClient;
  }
  sendExposureEvent(flagKey, validatedFlag, isExplicitlyRequested, exposureData) {
    const {
      evaluationDetail,
      trackingInfo = {
        samplingRate: 0
      }
    } = validatedFlag;

    // Only attach custom exposure data to opt-in exposure events.
    // This is done because the compression and storage for automatic exposure events do not currently
    // support custom attributes. By intentionally excluding them, we ensure that we are not collecting
    // data that will eventually be dropped further in the pipeline.
    // See https://hello.atlassian.net/wiki/spaces/MEASURE/pages/1316354396/RFC-002+Automatic+exposure+event+collection+in+the+Fx3+web+client for more information.
    const customAttributes = isExplicitlyRequested ? exposureData : {};
    const hasCustomAttributes = customAttributes ? Object.keys(customAttributes).length > 0 : false;
    const exposureAttributes = {
      flagKey,
      value: validatedFlag.value,
      reason: evaluationDetail === null || evaluationDetail === void 0 ? void 0 : evaluationDetail.reason,
      ruleId: evaluationDetail === null || evaluationDetail === void 0 ? void 0 : evaluationDetail.ruleId,
      errorKind: evaluationDetail === null || evaluationDetail === void 0 ? void 0 : evaluationDetail.errorKind
    };
    const allAttributes = {
      ...customAttributes,
      ...exposureAttributes
    };
    const shouldSendExposureEvent = this.shouldSendExposureEvent(allAttributes, trackingInfo, isExplicitlyRequested);
    if (shouldSendExposureEvent) {
      const actionSubject = 'feature';
      const action = 'exposed';
      const source = '@atlassiansox/feature-flag-web-client';
      const highPriority = isExplicitlyRequested;
      this.analyticsWebClient.sendOperationalEvent({
        actionSubject,
        action,
        attributes: exposureAttributes,
        tags: ['measurement', isExplicitlyRequested ? ExposureType.OptIn : ExposureType.Automatic],
        source,
        highPriority
      });

      // Due to the way we've simplified our pipeline, custom attributes are dropped from exposure
      // events. In order not to break existing users of this feature immediately, we're sending an
      // extra event that won't be consumed by our pipeline, but will end up in the `cloud.*` tables.
      // See: https://hello.atlassian.net/wiki/spaces/MEASURE/pages/1838596497/AtlasKit+TAC
      if (hasCustomAttributes) {
        this.analyticsWebClient.sendOperationalEvent({
          actionSubject,
          action,
          attributes: allAttributes,
          tags: ['measurement', ExposureType.OptIn, ExposureType.HasCustomAttributes],
          source,
          highPriority
        });
      }
      this.markExposureAsTracked(exposureAttributes);
    }
  }
  shouldSendExposureEvent(exposureAttributes, trackingInfo, isExplicitlyRequested) {
    if (this.hasAlreadyTrackedExposure(exposureAttributes)) {
      return false;
    }
    if (isExplicitlyRequested) {
      return true;
    }
    const samplingRate = trackingInfo.samplingRate;
    const shouldSendAutomaticEvent = this.automaticExposuresEnabled && samplingRate === 1 && this.automaticExposuresPredicate(exposureAttributes);
    return shouldSendAutomaticEvent;
  }
  hasAlreadyTrackedExposure(attributes) {
    const hash = hashFn(attributes);
    if (this.spamBuster[hash] === undefined || this.spamBuster[hash] + ONE_HOUR < Date.now()) {
      return false;
    }
    return true;
  }
  markExposureAsTracked(attributes) {
    const hash = hashFn(attributes);
    this.spamBuster[hash] = Date.now();
  }
  setAutomaticExposuresEnabled(enabled, predicate = () => true) {
    this.automaticExposuresEnabled = enabled;
    this.automaticExposuresPredicate = predicate;
  }
}